import { Directive, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudDataSource, CrudListComponent, FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_TYPE, BusinessUnit, RefCode } from 'assets-model';
import { BusinessUnitDataSource, BusinessUnitService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class BusinessUnitSearchComponentBase extends CrudListComponent<BusinessUnit, string> {
  protected systemConfig: any;

  displayedColumns: string[] = [ 'buId', 'acronym', 'closed', 'createdBy', 'createdDate', 'lockVersion', 'name', 'national', 'territory', 'type', 'updatedBy', 'updatedDate', 'actions'];

  public searchForm: FormGroup;

  buIdFilter: string;
  @ViewChild('buIdFilterInput') buIdFilterInput: ElementRef;

  acronymFilter: string;
  @ViewChild('acronymFilterInput') acronymFilterInput: ElementRef;

  closedFilter: boolean;
  @ViewChild('closedFilterInput') closedFilterInput: MatSelect;

  createdByFilter: string;
  @ViewChild('createdByFilterInput') createdByFilterInput: ElementRef;

  createdDateFilter: Date;
  @ViewChild('createdDateFilterInput') createdDateFilterInput: ElementRef;
  createdDateLessThanFilter: Date;
  @ViewChild('createdDateLessThanFilterInput') createdDateLessThanFilterInput: ElementRef;
  createdDateLessThanOrEqualToFilter: Date;
  @ViewChild('createdDateLessThanOrEqualToFilterInput') createdDateLessThanOrEqualToFilterInput: ElementRef;
  createdDateGreaterThanFilter: Date;
  @ViewChild('createdDateGreaterThanFilterInput') createdDateGreaterThanFilterInput: ElementRef;
  createdDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('createdDateGreaterThanOrEqualToFilterInput') createdDateGreaterThanOrEqualToFilterInput: ElementRef;

  nameFilter: string;
  @ViewChild('nameFilterInput') nameFilterInput: ElementRef;

  territoryFilter: string;
  @ViewChild('territoryFilterInput') territoryFilterInput: ElementRef;

  typeSelectionList: RefCode[];
  typeFilter: string;
  @ViewChild('typeFilterInput') typeFilterInput: MatSelect;

  updatedByFilter: string;
  @ViewChild('updatedByFilterInput') updatedByFilterInput: ElementRef;

  updatedDateFilter: Date;
  @ViewChild('updatedDateFilterInput') updatedDateFilterInput: ElementRef;
  updatedDateLessThanFilter: Date;
  @ViewChild('updatedDateLessThanFilterInput') updatedDateLessThanFilterInput: ElementRef;
  updatedDateLessThanOrEqualToFilter: Date;
  @ViewChild('updatedDateLessThanOrEqualToFilterInput') updatedDateLessThanOrEqualToFilterInput: ElementRef;
  updatedDateGreaterThanFilter: Date;
  @ViewChild('updatedDateGreaterThanFilterInput') updatedDateGreaterThanFilterInput: ElementRef;
  updatedDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('updatedDateGreaterThanOrEqualToFilterInput') updatedDateGreaterThanOrEqualToFilterInput: ElementRef;


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected businessUnitService: BusinessUnitService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(BUSINESS_UNIT_TYPE, route, router, userStorage, dialog, businessUnitService, injector);

    this.searchForm = this.fb.group({
      buId: [null],
      acronym: [null],
      closed: [null],
      createdBy: [null],
      createdDateLessThan: [null],
      createdDateLessThanOrEqualTo: [null],
      createdDateGreaterThan: [null],
      createdDateGreaterThanOrEqualTo: [null],
      createdDate: [null],
      name: [null],
      territory: [null],
      type: [null],
      updatedBy: [null],
      updatedDateLessThan: [null],
      updatedDateLessThanOrEqualTo: [null],
      updatedDateGreaterThan: [null],
      updatedDateGreaterThanOrEqualTo: [null],
      updatedDate: [null]
    });
  }

  createDataSource(): CrudDataSource<BusinessUnit> {
    return new BusinessUnitDataSource(this.businessUnitService);
  }

  createServiceArguments(): any {
    return {
      buId: this.buIdFilter,
      acronym: this.acronymFilter,
      closed: this.closedFilter,
      createdBy: this.createdByFilter,
      createdDateLessThan: this.createdDateLessThanFilter,
      createdDateLessThanOrEqualTo: this.createdDateLessThanOrEqualToFilter,
      createdDateGreaterThan: this.createdDateGreaterThanFilter,
      createdDateGreaterThanOrEqualTo: this.createdDateGreaterThanOrEqualToFilter,
      createdDate: this.createdDateFilter,
      name: this.nameFilter,
      territory: this.territoryFilter,
      type: this.typeFilter,
      updatedBy: this.updatedByFilter,
      updatedDateLessThan: this.updatedDateLessThanFilter,
      updatedDateLessThanOrEqualTo: this.updatedDateLessThanOrEqualToFilter,
      updatedDateGreaterThan: this.updatedDateGreaterThanFilter,
      updatedDateGreaterThanOrEqualTo: this.updatedDateGreaterThanOrEqualToFilter,
      updatedDate: this.updatedDateFilter
    };
  }
  
  clearFiltersBeforeLoadPage() {
    this.restoreFormFromParams(this.searchForm);
  }
  
  clearFilterVariables() {
    this.buIdFilter = null;
    this.acronymFilter = null;
    this.closedFilter = null;
    this.createdByFilter = null;
    this.createdDateLessThanFilter = null;
    this.createdDateLessThanOrEqualToFilter = null;
    this.createdDateGreaterThanFilter = null;
    this.createdDateGreaterThanOrEqualToFilter = null;
    this.createdDateFilter = null;
    this.nameFilter = null;
    this.territoryFilter = null;
    this.typeFilter = null;
    this.updatedByFilter = null;
    this.updatedDateLessThanFilter = null;
    this.updatedDateLessThanOrEqualToFilter = null;
    this.updatedDateGreaterThanFilter = null;
    this.updatedDateGreaterThanOrEqualToFilter = null;
    this.updatedDateFilter = null;
  }
  
  clearFilters() {
    this.clearFilterVariables();
    this.clearFiltersBeforeLoadPage();
    this.loadPage();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedInputEventListener(this.buIdFilterInput);
    this.addSearchOnChangedInputEventListener(this.acronymFilterInput);
    this.addSearchOnChangedSelectEventListener(this.closedFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdByFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateFilterInput);
    this.addSearchOnChangedInputEventListener(this.nameFilterInput);
    this.addSearchOnChangedInputEventListener(this.territoryFilterInput);
    this.addSearchOnChangedSelectEventListener(this.typeFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedByFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateFilterInput);
    this.formStorage.rememberFormChanges(this.searchForm);
  }

  ngOnInit() {
    super.ngOnInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }

  protected initExportOptions(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initExportOptions(config);
  }
  
  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    return {
      buId: (value: string) => self.buIdFilter = value,
      acronym: (value: string) => self.acronymFilter = value,
      closed: (value: boolean) => self.closedFilter = value,
      createdBy: (value: string) => self.createdByFilter = value,
      createdDateLessThan: (value: Date) => self.createdDateLessThanFilter = value,
      createdDateLessThanOrEqualTo: (value: Date) => self.createdDateLessThanOrEqualToFilter = value,
      createdDateGreaterThan: (value: Date) => self.createdDateGreaterThanFilter = value,
      createdDateGreaterThanOrEqualTo: (value: Date) => self.createdDateGreaterThanOrEqualToFilter = value,
      createdDate: (value: Date) => self.createdDateFilter = value,
      name: (value: string) => self.nameFilter = value,
      territory: (value: string) => self.territoryFilter = value,
      type: (value: string) => self.typeFilter = value,
      updatedBy: (value: string) => self.updatedByFilter = value,
      updatedDateLessThan: (value: Date) => self.updatedDateLessThanFilter = value,
      updatedDateLessThanOrEqualTo: (value: Date) => self.updatedDateLessThanOrEqualToFilter = value,
      updatedDateGreaterThan: (value: Date) => self.updatedDateGreaterThanFilter = value,
      updatedDateGreaterThanOrEqualTo: (value: Date) => self.updatedDateGreaterThanOrEqualToFilter = value,
      updatedDate: (value: Date) => self.updatedDateFilter = value
    }
  }
  
  restoreFormChanges() {
    const self = this;

    // IMPORTANT that the restoreFormChanges call occurs before the initial search - typically best done in onInitAsyncComplete
    // Whereever a form control binds with [ngModel] to a reactive form, ngModel takes precedence,
    // and needs it's own setter function to set the scope attribute bound
    this.formStorage.restoreFormChanges(this.searchForm, this.transformAllPropertiesToModelSetters());
  }

  buIdChanging(value: string, unsetDependents: boolean = true) {}
  
  buIdChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  acronymChanging(value: string, unsetDependents: boolean = true) {}
  
  acronymChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  closedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  closedChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  nameChanging(value: string, unsetDependents: boolean = true) {}
  
  nameChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  territoryChanging(value: string, unsetDependents: boolean = true) {}
  
  territoryChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  typeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('BU_TYPE').subscribe(
      data => {
        this.typeSelectionList = data;
      }
    );
  }

  typeChanging(value: string, unsetDependents: boolean = true) {}
  
  typeChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disabledTooltip(element) {
    if (!element) {
      return true;
    }
    return element.valid || !element.touched;
  }

  get buId() {
    return this.searchForm && this.searchForm.get('buId');
  }

  get acronym() {
    return this.searchForm && this.searchForm.get('acronym');
  }

  get closed() {
    return this.searchForm && this.searchForm.get('closed');
  }

  get createdBy() {
    return this.searchForm && this.searchForm.get('createdBy');
  }

  get createdDate() {
    return this.searchForm && this.searchForm.get('createdDate');
  }

  get createdDateLessThan() {
    return this.searchForm && this.searchForm.get('createdDateLessThan');
  }
  
  get createdDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('createdDateLessThanOrEqualTo');
  }
  
  get createdDateGreaterThan() {
    return this.searchForm && this.searchForm.get('createdDateGreaterThan');
  }
  
  get createdDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('createdDateGreaterThanOrEqualTo');
  }

  get name() {
    return this.searchForm && this.searchForm.get('name');
  }

  get territory() {
    return this.searchForm && this.searchForm.get('territory');
  }

  get type() {
    return this.searchForm && this.searchForm.get('type');
  }

  get updatedBy() {
    return this.searchForm && this.searchForm.get('updatedBy');
  }

  get updatedDate() {
    return this.searchForm && this.searchForm.get('updatedDate');
  }

  get updatedDateLessThan() {
    return this.searchForm && this.searchForm.get('updatedDateLessThan');
  }
  
  get updatedDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('updatedDateLessThanOrEqualTo');
  }
  
  get updatedDateGreaterThan() {
    return this.searchForm && this.searchForm.get('updatedDateGreaterThan');
  }
  
  get updatedDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('updatedDateGreaterThanOrEqualTo');
  }


}



